import { AlertTriangle, Check } from 'react-feather';
import { ComponentProps, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Div } from './Generic';
import { Icon } from './Icon';
import { UIProps } from '../../theme/mixins';
import SC from './SC';

interface AlertProps extends SC<AlertContainerProps> {
  icon?: ReactNode | null;
  children?: ReactNode;
}

export const Success = ({
  icon = <Check />, children, sc, ...props
}: AlertProps & ComponentProps<typeof AlertContainer>) => (
  <AlertContainer sc={{ icon: !!icon, type: 'success', ...sc }} {...props}>
    {icon && (
      <Icon>
        {icon}
      </Icon>
    )}
    <Div>
      {children}
    </Div>
  </AlertContainer>
);

export const Info = ({
  icon, children, sc, ...props
}: AlertProps & ComponentProps<typeof AlertContainer>) => (
  <AlertContainer sc={{ icon: !!icon, type: 'info', ...sc }} {...props}>
    {icon && (
      <Icon>
        {icon}
      </Icon>
    )}
    <Div>
      {children}
    </Div>
  </AlertContainer>
);

export const Neutral = ({
  icon, children, sc, ...props
}: AlertProps & ComponentProps<typeof AlertContainer>) => (
  <AlertContainer sc={{ icon: !!icon, type: 'neutral', ...sc }} {...props}>
    {icon && (
      <Icon>
        {icon}
      </Icon>
    )}
    <Div>
      {children}
    </Div>
  </AlertContainer>
);

export const Warning = ({
  icon = <AlertTriangle />, children, sc, ...props
}: AlertProps & ComponentProps<typeof AlertContainer>) => (
  <AlertContainer sc={{ icon: !!icon, type: 'warning', ...sc }} {...props}>
    {icon && (
      <Icon>
        {icon}
      </Icon>
    )}
    <Div>
      {children}
    </Div>
  </AlertContainer>
);

export const Error = ({
  icon = <AlertTriangle />, children, sc, ...props
}: AlertProps & ComponentProps<typeof AlertContainer>) => (
  <AlertContainer sc={{ icon: !!icon, type: 'error', ...sc }} {...props}>
    {icon && (
      <Icon>
        {icon}
      </Icon>
    )}
    <Div>
      {children}
    </Div>
  </AlertContainer>
);

interface AlertContainerProps extends UIProps {
  type?: 'success' | 'info' | 'neutral' | 'warning' | 'error' | 'atleta';
  icon?: boolean;
  rounded?: boolean;
}

export const AlertContainer = styled(Div)<SC<AlertContainerProps>>`
  ${({ sc: { type = 'info', icon = false, rounded = false } = {}, theme }) => {
    const backgroundColors = {
      success: theme.getColor('primary.25'),
      info: theme.transparentize(theme.getColor('secondary.100'), 0.7),
      neutral: theme.getColor('gray.75'),
      warning: theme.getColor('info.100'),
      error: theme.getColor('error.50'),
      atleta: theme.getColor('atleta_secondary.50'),
    };
    const accentColors = {
      success: theme.getColor('primary'),
      info: theme.getColor('secondary.400'),
      neutral: theme.getColor('gray.600'),
      warning: theme.getColor('info.600'),
      error: theme.getColor('error.500'),
      atleta: theme.getColor('atleta_secondary.400'),
    };

    return css`
      background: ${backgroundColors[type]};
      border-left: 3px solid ${accentColors[type]};
      padding: ${theme.gutter / 2}px;
      transition: all 0.15s ease-in-out;

      h1, h2, h3, h4, h5, h6 {
        font-weight: 700;
        margin: 1px 0;
      }

      strong {
        font-weight: 600;
      }

      ${rounded && css`
        border-radius: ${theme.borderRadiuses.xl}px;
      `}

      ${icon && css`
        display: flex;
        justify-content: flex-start;

        > ${Icon}:first-child {
          min-width: 28px;
          width: 28px;
          color: ${accentColors[type]};
          transition: all 0.15s ease-in-out;
          margin: 1.5px 0 0 0;

          svg {
            width: 16px;
            height: 16px;
          }
        }

        > :nth-child(2) {
          width: 100%;
        }
      `}
    `;
  }}
`;

export default undefined;
